import React, { createContext, useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import useStickyState from '@hooks/useStickyState'
import IUser from '@interfaces/IUser'
import * as $Auth from '@services/Auth'
import * as $Sanctum from '@services/Sanctum'
import { Outlet, useNavigate } from 'react-router-dom'
import * as $Donor from '@services/Donor'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

type AuthContextProps = {
  isLoading: boolean
  isAuthenticated: boolean
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
  user: IUser|null
  setUser: React.Dispatch<React.SetStateAction<IUser|null>>
  favoritesCount: number
  setFavoritesCount: React.Dispatch<React.SetStateAction<number>>
  token: string|null
  setToken: React.Dispatch<React.SetStateAction<string|null>>
  logout: () => void
  onFavoriteDonor: (donorId: string, callback: (isFavorited: boolean) => void) => Promise<void>
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps)

export const AuthProvider: React.FC = () => {
  const [ token, setToken ] = useStickyState<string|null>(null, 'token')
  const [ user, setUser ] = useState<IUser|null>(null)
  const [ favoritesCount, setFavoritesCount ] = useState<number>(0)
  const [ isAuthenticated, setIsAuthenticated ] = useState<boolean>(false)
  const [ isLoading, setIsLoading ] = useState<boolean>(token !== null && token.length > 0)

  const navigate = useNavigate()

  const SweetAlert = withReactContent(Swal)

  useEffect(() => {
    $Sanctum.csrf()
  }, [])

  useEffect(() => {
    if (token?.length) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      $Auth.me().then(({ data: user }) => {
        setUser(user)
        setFavoritesCount(user.favorites_count || 0)
        setIsAuthenticated(true)
      }).catch(() => setToken(null)).finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [setToken, token])

  const logout = useCallback(() => {
    setUser(null)
    setFavoritesCount(0)
    setToken('')
    setIsAuthenticated(false)
    navigate('/')
  }, [navigate, setToken])

  const onFavoriteDonor = useCallback(async (donorId: string, callback?: (isFavorited: boolean) => void): Promise<void> => {
    return $Donor.favorite(donorId).then(({ data: { is_favorited: isFavorited, favorites_count: favoritesCount }}) => {

      setFavoritesCount(favoritesCount)

      if (callback) callback(isFavorited)

      SweetAlert.fire({
        title: isFavorited ? `Doador ${donorId} favoritado` : `Doador ${donorId} desfavoritado`,
        icon: 'success',
      })
    }).catch(() => {
      SweetAlert.fire({
        title: `Erro ao favoritar doador ${donorId}`,
        icon: 'error',
        text: `Ocorreu um erro ao favoritar o doador ${donorId}. Tente novamente mais tarde.`,
      })
    })
  }, [SweetAlert])

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        favoritesCount,
        setFavoritesCount,
        token,
        setToken,
        logout,
        onFavoriteDonor,
      }}
    >
      <Outlet />
    </AuthContext.Provider>

  )
}

export default AuthContext
